import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Cpu, Plus, Link2Off, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { errorCodeLibrariesApi } from '../services/api';

/**
 * Renders the OEM error-code libraries linked to an asset, with controls
 * for linking new ones and unlinking existing ones. Lives inside the asset
 * detail drawer on the desktop Assets page.
 */
export const AssetErrorCodeLibrariesCard = ({ asset, onChanged }) => {
  // Track linked IDs locally so we can mutate on link/unlink without
  // needing the parent to re-fetch the asset. We seed from props on mount
  // and on every asset change (when the drawer opens a different asset).
  const [linkedIds, setLinkedIds] = useState(asset?.error_code_library_ids || []);
  const [allLibraries, setAllLibraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    setLinkedIds(asset?.error_code_library_ids || []);
  }, [asset?.id, asset?.error_code_library_ids]);

  useEffect(() => {
    (async () => {
      try {
        const res = await errorCodeLibrariesApi.list();
        setAllLibraries(Array.isArray(res.data) ? res.data : []);
      } catch {
        // Quietly fail — the card just renders empty.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const linked = allLibraries.filter(l => linkedIds.includes(l.id));
  const linkable = allLibraries.filter(l => !linkedIds.includes(l.id) && l.status === 'ready');

  const unlink = async (lib) => {
    setBusyId(lib.id);
    try {
      await errorCodeLibrariesApi.unlinkFromAsset(asset.id, lib.id);
      setLinkedIds(prev => prev.filter(id => id !== lib.id));
      toast.success(`Unlinked "${lib.name}"`);
      onChanged?.();
    } catch {
      toast.error('Unlink failed');
    } finally {
      setBusyId(null);
    }
  };

  const link = async (lib) => {
    setBusyId(lib.id);
    try {
      await errorCodeLibrariesApi.linkToAsset(asset.id, lib.id);
      setLinkedIds(prev => [...prev, lib.id]);
      toast.success(`Linked "${lib.name}"`);
      setPicker(false);
      onChanged?.();
    } catch {
      toast.error('Link failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="shadow-none" data-testid="asset-error-code-libraries-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="as-section">
            <Cpu className="w-3.5 h-3.5 text-[#C9A400]" />
            Linked OEM Manuals
            {linked.length > 0 && (
              <Badge className="lm-count ml-0.5 h-5 min-w-[20px] justify-center px-1.5 text-[11px]">{linked.length}</Badge>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPicker(true)}
            disabled={loading}
            data-testid="link-error-code-lib-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Link manual
          </Button>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed">
          Operators scanning this asset's QR can look up fault codes from any linked OEM manual.
          Upload new manuals from <span className="text-foreground">Settings → Error Codes</span>.
        </p>

        {loading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : linked.length === 0 ? (
          <div className="border border-dashed border-border rounded-md p-4 text-center" data-testid="asset-ecl-empty">
            <p className="text-xs text-muted-foreground">
              No manuals linked yet. Click <strong>Link manual</strong> to attach one.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {linked.map(lib => (
              <li
                key={lib.id}
                className="flex items-center justify-between gap-3 p-3 rounded-md bg-[#FBFAF7] border border-border"
                data-testid={`asset-ecl-linked-${lib.id}`}
              >
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{lib.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {[lib.make, lib.model].filter(Boolean).join(' · ') || '—'}
                    {' · '}
                    {lib.status === 'ready' ? (
                      <span className="text-emerald-600 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />{lib.code_count} codes
                      </span>
                    ) : (
                      <span className="text-amber-600 inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />{lib.status}
                      </span>
                    )}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => unlink(lib)}
                  disabled={busyId === lib.id}
                  data-testid={`asset-ecl-unlink-${lib.id}`}
                  title="Unlink"
                >
                  {busyId === lib.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Link2Off className="w-4 h-4 text-red-500" />
                  )}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {picker && (
        <Dialog open onOpenChange={(v) => !v && setPicker(false)}>
          <DialogContent data-testid="asset-ecl-picker-dialog">
            <DialogHeader>
              <DialogTitle>Link an OEM manual to {asset.unit_number || asset.name}</DialogTitle>
            </DialogHeader>
            <div className="py-2">
              {linkable.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">
                  No additional ready-state libraries. Go to{' '}
                  <span className="text-foreground">Settings → Error Codes</span> to upload one.
                </p>
              ) : (
                <ul className="space-y-2 max-h-80 overflow-y-auto">
                  {linkable.map(lib => (
                    <li
                      key={lib.id}
                      className="flex items-center justify-between gap-3 p-3 rounded-md bg-[#FBFAF7] border border-border"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{lib.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {[lib.make, lib.model].filter(Boolean).join(' · ') || '—'} · {lib.code_count} codes
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => link(lib)}
                        disabled={busyId === lib.id}
                        data-testid={`asset-ecl-link-option-${lib.id}`}
                      >
                        {busyId === lib.id ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4 mr-1" />
                        )}
                        Link
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPicker(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};
